import { Menu, MenuItem } from '@szhsin/react-menu';
import type { FC } from 'react';
import { toast } from 'react-hot-toast';

import {
    Button,
    ButtonVariant,
    theme,
    transformWorthString,
    IconName,
    Icon,
    ButtonSize,
    Toast,
} from '@catalyze/ui';
import type { ILead } from 'api/types/lead';
import { Status } from 'api/types/lead';

import {
    Container,
    Address,
    BasicInfo,
    BasicInfoItem,
    NetWorth,
    NetWorthValue,
    LeadState,
    ButtonsContainer,
    KebabIconButton,
    MenuItemIcon,
    OwnerTitle,
    AdditionalInfo,
} from './styled';

interface IPropertyInfo {
    propertyData: ILead;
    updateLeadStatus: (status: Status, id: number) => void;
    reset: (id: number) => void;
}

export const PropertyInfo: FC<IPropertyInfo> = ({
    propertyData,
    updateLeadStatus,
    reset,
}) => {
    const {
        address,
        state,
        city,
        zip,
        sqFoot,
        beds,
        bathrooms,
        categories,
        owners,
        estimatedMarketValue,
        status,
        propertyLeadId,
        leadType,
        obitFirstName,
        obitLastName,
    } = propertyData;

    const estateOf =
        obitFirstName || obitLastName
            ? `${obitFirstName} ${obitLastName}`
            : null;

    const fullAddress = `${address}, ${city} ${state} ${zip}`;

    return (
        <Container status={status}>
            <span>
                <Address style={{ float: 'left' }}>{fullAddress}</Address>
                <a
                    href={`https://google.com/search?q=${fullAddress}`}
                    title="Search on Google"
                    rel="noreferrer"
                    target="_blank"
                >
                    &nbsp;
                    <span role="img" aria-label="search">
                        🌐
                    </span>
                </a>
            </span>
            <BasicInfo>
                <BasicInfoItem>
                    <Icon iconName={IconName.SQFT} /> {`${sqFoot} sqft`}
                </BasicInfoItem>
                <BasicInfoItem>
                    <Icon iconName={IconName.BEDROOM} />
                    {`${beds} bedrooms`}
                </BasicInfoItem>
                <BasicInfoItem>
                    <Icon iconName={IconName.BATHROOM} />
                    {`${bathrooms} bathrooms`}
                </BasicInfoItem>
                <BasicInfoItem>{`Catalyst: ${categories}`}</BasicInfoItem>
            </BasicInfo>
            <AdditionalInfo>
                {!leadType ? null : (
                    <BasicInfoItem>{`Scenario: ${leadType}`}</BasicInfoItem>
                )}
                {!estateOf ? null : (
                    <BasicInfoItem>{`Estate Of: ${estateOf}`}</BasicInfoItem>
                )}
            </AdditionalInfo>
            <OwnerTitle>
                <Icon iconName={IconName.OWNER} />
                {owners.map((owner, idx) => {
                    return idx === owners.length - 1 ? owner : `${owner}, `;
                })}
            </OwnerTitle>
            <NetWorth>
                <NetWorthValue>
                    {transformWorthString(Number(estimatedMarketValue))}
                </NetWorthValue>
                estimated market value
            </NetWorth>
            <LeadState>{`${status} lead`}</LeadState>

            <ButtonsContainer>
                {status === Status.RECOMMENDED && (
                    <Button
                        size={ButtonSize.SMALL}
                        variant={ButtonVariant.PRIMARY}
                        color={theme.colors.bgPrimary}
                        bgColor={theme.colors.bgSecondary}
                        onClick={() => {
                            updateLeadStatus(Status.CONTACTED, propertyLeadId);

                            toast(
                                () => (
                                    <Toast
                                        title={`Lead marked as ${Status.CONTACTED}`}
                                    />
                                ),
                                {
                                    style: { backgroundColor: '#000' },
                                }
                            );
                        }}
                    >
                        {`Mark as ${Status.CONTACTED}`}
                    </Button>
                )}
                <Menu
                    menuButton={
                        <KebabIconButton
                            bgColor={theme.colors.bgTernary}
                            variant={ButtonVariant.SECONDARY}
                            iconName={IconName.MENU_DOTS}
                        />
                    }
                >
                    <MenuItem
                        onClick={() => {
                            reset(propertyLeadId);
                        }}
                    >
                        <MenuItemIcon iconName={IconName.RESET} /> Reset lead
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            updateLeadStatus(Status.ARCHIVED, propertyLeadId);
                        }}
                    >
                        <MenuItemIcon iconName={IconName.ARCHIVE} />
                        Archive lead
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            updateLeadStatus(Status.DELETED, propertyLeadId);
                        }}
                    >
                        <MenuItemIcon iconName={IconName.DELETE} />
                        Delete lead
                    </MenuItem>
                </Menu>
            </ButtonsContainer>
        </Container>
    );
};
